import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Eye, EyeOff, Loader2, Mail, ShieldCheck, Smartphone } from "lucide-react";
import logo from "#/assets/eatta-logo.png";
import { useAuth } from "#/context/AuthProvider";
import { sendEmailMfaChallenge } from "#/features/auth/api";
import { isMfaRequired } from "#/features/auth/types";
import { OtpInput } from "#/components/common/OtpInput";

function getApiErrorMessage(err: any) {
  return (
    err?.response?.data?.message ||
    err?.message ||
    "Something went wrong. Please try again."
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

type MfaChallenge = {
  challengeToken: string;
  availableMethods: ("EMAIL" | "TOTP")[];
  totpConfigured: boolean;
  emailMasked?: string | null;
};

type MfaView = "choose" | "email" | "totp";


// ── Background logo positions ──────────────────────────────────────────────────

const BG_LOGOS = [
  { top: "2%",  left: "4%",   w: 80,  rotate: -18, opacity: 0.07 },
  { top: "5%",  right: "5%",  w: 68,  rotate:  22, opacity: 0.06 },
  { top: "20%", left: "1%",   w: 56,  rotate:  -6, opacity: 0.05 },
  { top: "23%", right: "2%",  w: 88,  rotate:  12, opacity: 0.07 },
  { top: "42%", left: "2%",   w: 64,  rotate:  28, opacity: 0.05 },
  { top: "45%", right: "1%",  w: 52,  rotate: -22, opacity: 0.05 },
  { top: "60%", left: "6%",   w: 72,  rotate:   9, opacity: 0.06 },
  { top: "63%", right: "4%",  w: 60,  rotate: -10, opacity: 0.05 },
  { top: "76%", left: "1%",   w: 52,  rotate:  20, opacity: 0.04 },
  { top: "78%", right: "2%",  w: 80,  rotate:  -5, opacity: 0.06 },
  { top: "88%", left: "5%",   w: 68,  rotate: -14, opacity: 0.05 },
  { top: "90%", right: "7%",  w: 56,  rotate:  16, opacity: 0.05 },
  { top: "12%", left: "20%",  w: 44,  rotate:  35, opacity: 0.03 },
  { top: "52%", left: "16%",  w: 40,  rotate: -20, opacity: 0.03 },
  { top: "33%", right: "17%", w: 48,  rotate:  -8, opacity: 0.03 },
  { top: "70%", right: "15%", w: 44,  rotate:  24, opacity: 0.03 },
  { top: "82%", left: "24%",  w: 52,  rotate:  -6, opacity: 0.04 },
  { top: "95%", left: "42%",  w: 60,  rotate:  14, opacity: 0.04 },
];

// ── Root page ─────────────────────────────────────────────────────────────────

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, verifyMfa } = useAuth();

  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  const [mfaExpiresAt, setMfaExpiresAt]       = useState<number | null>(null);
  const [mfaCountdownAt, setMfaCountdownAt]   = useState<number | null>(null);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState<string | null>(null);

  // Countdown display: 5 minutes. Auto-reset to login: 6 minutes.
  useEffect(() => {
    if (!mfaChallenge) return;
    setMfaCountdownAt(Date.now() + 5 * 60 * 1000); // display counts down from 5:00
    setMfaExpiresAt(Date.now() + 6 * 60 * 1000);   // actual reset at 6 mins
    const timer = setTimeout(() => {
      setMfaChallenge(null);
      setMfaExpiresAt(null);
      setMfaCountdownAt(null);
      setError("Your verification session expired. Please sign in again.");
    }, 6 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [mfaChallenge]);

  async function handleCredentialsSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await login({ email: email.trim().toLowerCase(), password });
      if (isMfaRequired(response)) {
        const raw = response as any;
        const availableMethods: ("EMAIL" | "TOTP")[] =
          Array.isArray(raw.availableMethods) && raw.availableMethods.length > 0
            ? raw.availableMethods
            : raw.mfaType ? [raw.mfaType as "EMAIL" | "TOTP"] : ["EMAIL"];
        const totpConfigured: boolean =
          raw.totpConfigured ?? availableMethods.includes("TOTP");
        setMfaChallenge({
          challengeToken: response.challengeToken,
          availableMethods,
          totpConfigured,
          emailMasked: raw.emailMasked ?? null,
        });
        return;
      }
      if (response.status === "PASSWORD_CHANGE_REQUIRED") {
        navigate("/account-setup", { replace: true });
        return;
      }
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(code: string, method: "EMAIL" | "TOTP") {
    if (!mfaChallenge) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await verifyMfa({
        challengeToken: mfaChallenge.challengeToken,
        code,
        method,
      });
      if (response.status === "PASSWORD_CHANGE_REQUIRED") {
        navigate("/account-setup", { replace: true });
        return;
      }
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-200 px-4 pb-16 pt-8">

      {/* ── Background ────────────────────────────────────────────────────── */}
      <div className="pointer-events-none absolute inset-0 select-none">
        {/* Dot grid */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(circle, #64748b 1.2px, transparent 1.2px)",
            backgroundSize: "28px 28px",
            opacity: 0.25,
          }}
        />
        {/* Soft centre glow */}
        <div className="absolute left-1/2 top-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-200/30 blur-[110px]" />

        {/* Large logo — left of card */}
        <img
          src={logo} alt="" aria-hidden="true"
          style={{ opacity: 0.07 }}
          className="absolute left-[12%] top-1/2 -translate-y-1/2 w-[18vw] max-w-[200px] object-contain select-none pointer-events-none"
        />

        {/* Large logo — right of card */}
        <img
          src={logo} alt="" aria-hidden="true"
          style={{ opacity: 0.07 }}
          className="absolute right-[12%] top-1/2 -translate-y-1/2 w-[18vw] max-w-[200px] object-contain select-none pointer-events-none"
        />
        {/* Scattered logos */}
        {BG_LOGOS.map((p, i) => (
          <img
            key={i}
            src={logo}
            alt=""
            aria-hidden="true"
            style={{
              position:  "absolute",
              top:       p.top,
              left:      "left"  in p ? (p as any).left  : undefined,
              right:     "right" in p ? (p as any).right : undefined,
              width:     p.w,
              opacity:   p.opacity,
              transform: `rotate(${p.rotate}deg)`,
            }}
            className="object-contain"
          />
        ))}
      </div>

      {/* ── Centred column ────────────────────────────────────────────────── */}
      <div className="relative z-10 flex w-full max-w-sm flex-col items-center gap-4">

          {/* Logo + system name combined */}
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="inline-flex items-center gap-3 rounded-2xl bg-white px-5 py-3 shadow-md ring-1 ring-slate-200">
              <img src={logo} alt="EATTA" className="h-10 w-auto object-contain shrink-0" />
              <div className="text-left">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-600 leading-tight">
                  East African Tea Trade Association
                </p>
                <h1 className="mt-0.5 text-[15px] font-extrabold leading-tight text-slate-900 text-center">
                  Electronic Management System
                </h1>
              </div>
            </div>

            {/* Tagline */}
            <div className="flex items-center gap-2.5">
              <div className="h-px w-6 bg-emerald-400" />
              <p className="text-xs italic text-slate-500">Going Digital</p>
              <div className="h-px w-6 bg-emerald-400" />
            </div>
          </div>

          {/* Login card */}
          <div className="w-full rounded-3xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/80">
            {mfaChallenge ? (
              <MfaStepRoot
                challenge={mfaChallenge}
                onVerify={handleVerify}
                onBack={() => { setMfaChallenge(null); setMfaExpiresAt(null); setMfaCountdownAt(null); setError(null); }}
                submitting={submitting}
                externalError={error}
                onClearError={() => setError(null)}
                expiresAt={mfaCountdownAt}
              />
            ) : (
              <CredentialsStep
                email={email}
                password={password}
                onEmailChange={setEmail}
                onPasswordChange={setPassword}
                onSubmit={handleCredentialsSubmit}
                submitting={submitting}
                error={error}
              />
            )}
          </div>

          {/* Footer */}
          <div className="space-y-0.5 text-center">
            <p className="text-[11px] text-slate-400">
              © {new Date().getFullYear()} EATTA · All rights reserved
            </p>
            <p className="text-[11px] text-slate-400">Developed by Bryan Philip</p>
          </div>
      </div>
    </div>
  );
}

// ── Credentials form ──────────────────────────────────────────────────────────

function CredentialsStep({
  email, password,
  onEmailChange, onPasswordChange,
  onSubmit, submitting, error,
}: {
  email: string; password: string;
  onEmailChange: (v: string) => void; onPasswordChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void; submitting: boolean; error: string | null;
}) {
  const [showPw, setShowPw] = useState(false);

  return (
    <>
      <div className="mb-5 text-center">
        <h2 className="text-lg font-bold text-slate-900">Welcome back to EMS</h2>
        <p className="mt-0.5 text-sm text-slate-500">Sign in to your account to continue.</p>
      </div>

      <form className="space-y-4" onSubmit={onSubmit}>
        <div>
          <label className="mb-1.5 block pl-3 text-xs font-semibold text-slate-500">
            Username
          </label>
          <input
            type="text" value={email} onChange={(e) => onEmailChange(e.target.value)}
            required autoComplete="username"
            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            placeholder="info@eatta.co.ke"
          />
        </div>
        <div>
          <label className="mb-1.5 block pl-3 text-xs font-semibold text-slate-500">
            Password
          </label>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              value={password} onChange={(e) => onPasswordChange(e.target.value)}
              required autoComplete="current-password"
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 pr-11 text-sm text-slate-900 outline-none placeholder:text-slate-400 transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 transition hover:text-slate-600"
              tabIndex={-1}
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {error && <ErrorBox message={error} />}

        <button
          type="submit" disabled={submitting}
          className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in →"}
        </button>

        <div className="text-center">
          <Link to="/forgot-password" className="text-xs font-medium text-emerald-700 hover:underline">
            Forgot password?
          </Link>
        </div>
      </form>
    </>
  );
}

// ── MFA root ──────────────────────────────────────────────────────────────────

function MfaSessionCountdown({ expiresAt }: { expiresAt: number }) {
  const [secsLeft, setSecsLeft] = useState(() => Math.max(0, Math.round((expiresAt - Date.now()) / 1000)));

  useEffect(() => {
    const id = setInterval(() => {
      setSecsLeft(Math.max(0, Math.round((expiresAt - Date.now()) / 1000)));
    }, 1_000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const mins = Math.floor(secsLeft / 60);
  const secs = secsLeft % 60;
  // Grey → Blue → Green → Orange → Red as each minute passes
  const { text, dot, pulse } =
    secsLeft > 240 ? { text: "text-slate-400",  dot: "bg-slate-400",  pulse: false } :
    secsLeft > 180 ? { text: "text-blue-500",   dot: "bg-blue-500",   pulse: false } :
    secsLeft > 120 ? { text: "text-emerald-600",dot: "bg-emerald-500",pulse: false } :
    secsLeft > 60  ? { text: "text-amber-600",  dot: "bg-amber-500",  pulse: false } :
                     { text: "text-red-600",    dot: "bg-red-500",    pulse: true  };

  return (
    <div className={`mt-2 flex items-center justify-center gap-1.5 text-xs font-medium ${text}`}>
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot} ${pulse ? "animate-pulse" : ""}`} />
      Session expires in {mins}:{String(secs).padStart(2, "0")}
    </div>
  );
}

function MfaStepRoot({
  challenge, onVerify, onBack, submitting, externalError, onClearError, expiresAt,
}: {
  challenge: MfaChallenge;
  onVerify: (code: string, method: "EMAIL" | "TOTP") => Promise<void>;
  onBack: () => void; submitting: boolean;
  externalError: string | null; onClearError: () => void;
  expiresAt: number | null;
}) {
  const [view, setView] = useState<MfaView>("choose");

  function goBack() {
    if (view === "choose") { onBack(); }
    else { setView("choose"); onClearError(); }
  }

  return (
    <>
      <div className="mb-5 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <h2 className="text-lg font-bold text-slate-900">Verify your identity</h2>
        <p className="mt-1 text-sm text-slate-500">
          {view === "choose"
            ? "Choose how you'd like to complete sign-in."
            : view === "email"
            ? "Enter the code sent to your email."
            : "Enter the code from your authenticator app."}
        </p>
        {expiresAt && <MfaSessionCountdown expiresAt={expiresAt} />}
      </div>

      {view === "choose" && (
        <MethodChooser challenge={challenge} onChooseEmail={() => setView("email")} onChooseTotp={() => setView("totp")} />
      )}
      {view === "email" && (
        <EmailVerifyView challenge={challenge} onVerify={onVerify} submitting={submitting} externalError={externalError} />
      )}
      {view === "totp" && (
        <TotpVerifyView onVerify={onVerify} submitting={submitting} externalError={externalError} />
      )}

      <button
        type="button" onClick={goBack} disabled={submitting}
        className="mt-4 w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
      >
        {view === "choose" ? "← Back to sign in" : "← Choose a different method"}
      </button>
    </>
  );
}

// ── Method chooser ────────────────────────────────────────────────────────────

function MethodChooser({ challenge, onChooseEmail, onChooseTotp }: {
  challenge: MfaChallenge; onChooseEmail: () => void; onChooseTotp: () => void;
}) {
  const canUseTotp = challenge.availableMethods.includes("TOTP") && challenge.totpConfigured;

  return (
    <div className="space-y-2.5">
      <button onClick={onChooseEmail}
        className="group w-full rounded-xl border border-slate-200 bg-white p-3.5 text-left transition hover:border-blue-300 hover:bg-blue-50">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600 transition group-hover:bg-blue-200">
            <Mail className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-900">Email OTP</p>
            <p className="text-xs text-slate-500">
              {challenge.emailMasked ? `Code to ${challenge.emailMasked}` : "One-time code to your email"}
            </p>
          </div>
          <span className="text-xs font-medium text-blue-600 opacity-0 transition group-hover:opacity-100">→</span>
        </div>
      </button>

      <button onClick={canUseTotp ? onChooseTotp : undefined} disabled={!canUseTotp}
        className={`group w-full rounded-xl border p-3.5 text-left transition ${
          canUseTotp
            ? "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50"
            : "cursor-default border-slate-100 bg-slate-50 opacity-60"
        }`}>
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition ${
            canUseTotp ? "bg-emerald-100 text-emerald-600 group-hover:bg-emerald-200" : "bg-slate-200 text-slate-400"
          }`}>
            <Smartphone className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${canUseTotp ? "text-slate-900" : "text-slate-500"}`}>
              Authenticator App
            </p>
            {canUseTotp
              ? <p className="text-xs text-slate-500">Google Authenticator, Authy, or similar</p>
              : <p className="text-xs text-amber-600">Set up after signing in via email</p>
            }
          </div>
          {canUseTotp && (
            <span className="text-xs font-medium text-emerald-600 opacity-0 transition group-hover:opacity-100">→</span>
          )}
        </div>
      </button>
    </div>
  );
}

// ── Email verify view ─────────────────────────────────────────────────────────

function EmailVerifyView({ challenge, onVerify, submitting, externalError }: {
  challenge: MfaChallenge;
  onVerify: (code: string, method: "EMAIL") => Promise<void>;
  submitting: boolean; externalError: string | null;
}) {
  const [code, setCode]           = useState("");
  const [sending, setSending]     = useState(false);
  const [sent, setSent]           = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = useState(challenge.emailMasked ?? null);
  const didSend = useRef(false);

  useEffect(() => {
    if (didSend.current) return;
    didSend.current = true;
    setSending(true);
    sendEmailMfaChallenge({ challengeToken: challenge.challengeToken })
      .then((res) => { setSent(true); if (res.maskedEmail) setMaskedEmail(res.maskedEmail); })
      .catch((err) => setSendError(getApiErrorMessage(err)))
      .finally(() => setSending(false));
  }, [challenge.challengeToken]);

  useEffect(() => {
    if (code.replace(/ /g, "").length === 6 && !submitting && !sending) {
      void onVerify(code, "EMAIL").catch(() => setCode(""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  function handleResend() {
    setSendError(null); setSent(false); setSending(true); setCode("");
    sendEmailMfaChallenge({ challengeToken: challenge.challengeToken })
      .then((res) => { setSent(true); if (res.maskedEmail) setMaskedEmail(res.maskedEmail); })
      .catch((err) => setSendError(getApiErrorMessage(err)))
      .finally(() => setSending(false));
  }

  return (
    <div className="space-y-4">
      {sending && (
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin shrink-0 text-emerald-600" />
          Sending code to {maskedEmail ?? "your email"}…
        </div>
      )}
      {sent && !sending && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          Code sent to <span className="font-semibold">{maskedEmail ?? "your email"}</span>.
        </div>
      )}
      {sendError && <ErrorBox message={sendError} />}

      {(sent || sendError) && (
        <div className="space-y-3">
          <OtpInput value={code} onChange={setCode} autoFocus disabled={submitting} />
          {externalError && <ErrorBox message={externalError} />}
          <button type="button" onClick={() => void onVerify(code, "EMAIL")}
            disabled={submitting || code.length < 6}
            className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60">
            {submitting ? "Verifying…" : "Verify →"}
          </button>
          <div className="text-center">
            <button type="button" onClick={handleResend} disabled={sending || submitting}
              className="text-xs text-slate-500 hover:text-slate-700 hover:underline disabled:opacity-50">
              Didn't receive it? Resend code
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TOTP verify view ──────────────────────────────────────────────────────────

function TotpVerifyView({ onVerify, submitting, externalError }: {
  onVerify: (code: string, method: "TOTP") => Promise<void>;
  submitting: boolean; externalError: string | null;
}) {
  const [code, setCode] = useState("");

  useEffect(() => {
    if (code.replace(/ /g, "").length === 6 && !submitting) {
      void onVerify(code, "TOTP").catch(() => setCode(""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="space-y-4">
      <OtpInput value={code} onChange={(v) => setCode(v)} autoFocus disabled={submitting} />
      <div className="flex justify-center"><TotpWindowCountdown /></div>
      {externalError && <ErrorBox message={externalError} />}
      <button type="button" onClick={() => void onVerify(code, "TOTP")}
        disabled={submitting || code.length < 6}
        className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60">
        {submitting ? "Verifying…" : "Verify →"}
      </button>
      <p className="text-center text-xs text-slate-500">
        Lost access?{" "}
        <a href="mailto:itsupport@eatta.org" className="font-medium text-slate-600 hover:underline">
          Contact IT support
        </a>
      </p>
    </div>
  );
}

// ── TOTP countdown ring ───────────────────────────────────────────────────────

function TotpWindowCountdown() {
  const [s, setS] = useState(() => 30 - (Math.floor(Date.now() / 1000) % 30));
  useEffect(() => {
    const id = setInterval(() => setS(30 - (Math.floor(Date.now() / 1000) % 30)), 1_000);
    return () => clearInterval(id);
  }, []);
  const urgent = s <= 5;
  const r = 10, circ = 2 * Math.PI * r;
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium ${urgent ? "text-amber-600" : "text-slate-500"}`}>
      <svg width="24" height="24" viewBox="0 0 24 24" className="-rotate-90">
        <circle cx="12" cy="12" r={r} fill="none" stroke="#e2e8f0" strokeWidth="2.5" />
        <circle cx="12" cy="12" r={r} fill="none"
          stroke={urgent ? "#d97706" : "#10b981"} strokeWidth="2.5"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - s / 30)}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }}
        />
      </svg>
      <span>{urgent ? `Expires in ${s}s — wait for next` : `Refreshes in ${s}s`}</span>
    </div>
  );
}

// ── Error box ─────────────────────────────────────────────────────────────────

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
      {message}
    </div>
  );
}
